import { consentsSelector } from '@/features/configuration/configurationSelector';
import { useAuth } from '@/hooks/useAuth';
import { TransText } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import { cn } from '@/utils/cn';
import { sanitizeMarkup } from '@/utils/sanitizeMarkup';
import {
  Button,
  Card,
  Checkbox,
  Divider,
  Icons,
  Typography,
} from '@fleet/webshop-ui';
import { useCallback, useMemo, type FC } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

interface ConsentFormValues {
  consents: {
    id: number;
    code?: string;
    isMandatory: boolean;
    description?: string;
    checked: boolean;
  }[];
}

interface AccountDetailsSettingsProps {}

const AccountDetailsSettings: FC<AccountDetailsSettingsProps> = () => {
  const { logout } = useAuth();
  const consents = useSelector(consentsSelector);

  const defaultValues = useMemo<ConsentFormValues>(
    () => ({
      consents:
        consents?.items?.map(({ id, isMandatory, description, code }) => ({
          id,
          isMandatory,
          description,
          code,
          checked: isMandatory,
        })) ?? [],
    }),
    [consents]
  );

  const { control, handleSubmit, watch } = useForm<ConsentFormValues>({
    defaultValues,
  });

  const { fields, update } = useFieldArray({
    control,
    name: 'consents',
  });

  const watchedConsents = watch('consents');
  const hasChanges = useMemo(
    () =>
      watchedConsents.some(
        (consent, index) =>
          consent.checked !== defaultValues.consents[index]?.checked
      ),
    [watchedConsents, defaultValues.consents]
  );

  const onSubmit = useCallback((data: ConsentFormValues) => {
    const consentDecisions = data.consents.map((consent) => ({
      code: consent.code,
      decisionId: consent.checked
        ? 'CONSENT_DECISION.ACCEPTED'
        : 'CONSENT_DECISION.NOT_ACCEPTED',
    }));

    // TODO: Replace with API call when endpoint is available
    console.log('Consent decisions:', consentDecisions);
  }, []);

  return (
    <div className="flex h-fit w-full flex-col gap-4 text-dark">
      <Card className="w-full rounded-lg px-6 pb-6 pt-4">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <Icons.lock className="h-6 w-6" />
            <Typography variant="heading2" as="h2">
              <TransText i18nKey="loginDetails" />
            </Typography>
          </div>

          <Divider />

          <Typography variant="body1">TODO: BR-55910</Typography>
        </div>
      </Card>

      <Card className="">
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <Icons.userCheck className="h-6 w-6" />
            <Typography variant="heading2" as="h2">
              <TransText i18nKey="consents" />
            </Typography>
          </div>

          <Divider />

          <div className="flex flex-col gap-4" data-testid="account-consents">
            {fields.map((consent, index) => {
              const markup = consent.description || '';

              return (
                <div key={consent.id} className="flex items-center gap-3">
                  <Checkbox
                    size="lg"
                    isSelected={consent.checked}
                    isDisabled={consent.isMandatory}
                    onChange={(checked) =>
                      update(index, { ...consent, checked })
                    }
                  />
                  <div className="flex flex-wrap items-center gap-0.5">
                    {markup ? (
                      <Typography
                        dangerouslySetInnerHTML={{
                          __html: sanitizeMarkup(markup),
                        }}
                        variant="body1"
                        className={cn('text-dark prose-a:text-blue-600')}
                      />
                    ) : (
                      <Typography variant="body1" className="text-dark">
                        <TransText i18nKey="undefinedConsentDescription" />
                      </Typography>
                    )}
                    {!consent.isMandatory && (
                      <Typography variant="body2" className="text-neutral">
                        (<TransText i18nKey="optional" />)
                      </Typography>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div>
            <Button
              type="submit"
              variant="secondary"
              className="w-fit gap-2"
              data-testid="save-consents-button"
              startAdornment={<Icons.check className="h-5 w-5" />}
              isDisabled={!hasChanges}
            >
              <TransText i18nKey="save" />
            </Button>
          </div>
        </form>
      </Card>

      <Button
        variant="secondary"
        className="mt-4 w-fit gap-2"
        startAdornment={<Icons.logOut className="h-5 w-5" />}
        onClick={logout}
        data-testid="logout-button"
      >
        <TransText i18nKey="logOut" />
      </Button>
    </div>
  );
};

export default AccountDetailsSettings;
